import { supabase } from './supabase';

export type ActivityType = 'call' | 'email' | 'meeting' | 'note' | 'task';

export interface Activity {
  id: string;
  brand_id: string;
  opportunity_id: string;
  activity_type: ActivityType;
  title: string;
  description?: string;
  duration_minutes?: number;
  outcome?: string;
  activity_date?: string;
  scheduled_at?: string;
  completed_at?: string;
  is_completed: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateActivityInput {
  opportunity_id: string;
  brand_id: string;
  activity_type: ActivityType;
  title: string;
  description?: string;
  duration_minutes?: number;
  outcome?: string;
  activity_date?: string;
  scheduled_at?: string;
  is_completed?: boolean;
}

export interface UpdateActivityInput {
  title?: string;
  description?: string;
  duration_minutes?: number;
  outcome?: string;
  scheduled_at?: string;
  completed_at?: string;
  is_completed?: boolean;
}

export async function getActivitiesByOpportunity(opportunityId: string): Promise<Activity[]> {
  const { data, error } = await supabase
    .from('activities')
    .select('*')
    .eq('opportunity_id', opportunityId)
    .order('activity_date', { ascending: false });

  if (error) {
    console.error('Error fetching activities:', error);
    return [];
  }

  return data || [];
}

export async function getActivitiesByBrand(
  brandId: string,
  filters?: {
    activity_type?: ActivityType;
    is_completed?: boolean;
    scheduled_after?: string;
    scheduled_before?: string;
  }
): Promise<Activity[]> {
  let query = supabase
    .from('activities')
    .select('*')
    .eq('brand_id', brandId);

  if (filters?.activity_type) {
    query = query.eq('activity_type', filters.activity_type);
  }

  if (filters?.is_completed !== undefined) {
    query = query.eq('is_completed', filters.is_completed);
  }

  if (filters?.scheduled_after) {
    query = query.gte('scheduled_at', filters.scheduled_after);
  }

  if (filters?.scheduled_before) {
    query = query.lte('scheduled_at', filters.scheduled_before);
  }

  query = query.order('activity_date', { ascending: false });

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching activities:', error);
    return [];
  }

  return data || [];
}

export async function getUpcomingTasks(brandId: string): Promise<Activity[]> {
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from('activities')
    .select('*')
    .eq('brand_id', brandId)
    .eq('activity_type', 'task')
    .eq('is_completed', false)
    .gte('scheduled_at', now)
    .order('scheduled_at', { ascending: true });

  if (error) {
    console.error('Error fetching upcoming tasks:', error);
    return [];
  }

  return data || [];
}

export async function getOverdueTasks(brandId: string): Promise<Activity[]> {
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from('activities')
    .select('*')
    .eq('brand_id', brandId)
    .eq('activity_type', 'task')
    .eq('is_completed', false)
    .lt('scheduled_at', now)
    .order('scheduled_at', { ascending: true });

  if (error) {
    console.error('Error fetching overdue tasks:', error);
    return [];
  }

  return data || [];
}

export async function createActivity(input: CreateActivityInput): Promise<Activity | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    console.error('No authenticated user');
    return null;
  }

  const activityData: any = {
    ...input,
    created_by: user.id,
  };

  if (input.is_completed) {
    activityData.completed_at = new Date().toISOString();
  }

  const { data, error } = await supabase
    .from('activities')
    .insert(activityData)
    .select('*')
    .maybeSingle();

  if (error) {
    console.error('Error creating activity:', error);
    return null;
  }

  return data;
}

export async function updateActivity(
  activityId: string,
  updates: UpdateActivityInput
): Promise<boolean> {
  const updateData: any = { ...updates };

  if (updates.is_completed && !updates.completed_at) {
    updateData.completed_at = new Date().toISOString();
  } else if (updates.is_completed === false) {
    updateData.completed_at = null;
  }

  const { error } = await supabase
    .from('activities')
    .update(updateData)
    .eq('id', activityId);

  if (error) {
    console.error('Error updating activity:', error);
    return false;
  }

  return true;
}

export async function deleteActivity(activityId: string): Promise<boolean> {
  const { error } = await supabase.from('activities').delete().eq('id', activityId);

  if (error) {
    console.error('Error deleting activity:', error);
    return false;
  }

  return true;
}

export async function getActivityStats(brandId: string, startDate?: string, endDate?: string) {
  let query = supabase
    .from('activities')
    .select('*')
    .eq('brand_id', brandId);

  if (startDate) {
    query = query.gte('activity_date', startDate);
  }

  if (endDate) {
    query = query.lte('activity_date', endDate);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching activity stats:', error);
    return {
      totalActivities: 0,
      activitiesByType: {},
      completedTasks: 0,
      pendingTasks: 0,
      overdueTasks: 0,
    };
  }

  const activities = data || [];
  const now = new Date();

  const activitiesByType = activities.reduce((acc: any, activity) => {
    acc[activity.activity_type] = (acc[activity.activity_type] || 0) + 1;
    return acc;
  }, {});

  const tasks = activities.filter((a) => a.activity_type === 'task');
  const completedTasks = tasks.filter((t) => t.is_completed).length;
  const pendingTasks = tasks.filter((t) => !t.is_completed).length;
  const overdueTasks = tasks.filter(
    (t) => !t.is_completed && t.scheduled_at && new Date(t.scheduled_at) < now
  ).length;

  return {
    totalActivities: activities.length,
    activitiesByType,
    completedTasks,
    pendingTasks,
    overdueTasks,
  };
}
